import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { PlatformProvider, usePlatform } from './context/PlatformContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import Map from './pages/Map';
import ReportWaste from './pages/ReportWaste';
import WasteDetail from './pages/WasteDetail';
import Dashboard from './pages/Dashboard';
import IndividualDashboard from './pages/IndividualDashboard';
import CorporateDashboard from './pages/CorporateDashboard';
import Profile from './pages/Profile';
import Account from './pages/Account';
import Subscribe from './pages/Subscribe';
import PaymentSuccess from './pages/PaymentSuccess';
import Pricing from './pages/Pricing';
import DevUserSwitcher from './components/DevUserSwitcher';
import SuperAdminLogin from './pages/super-admin/SuperAdminLogin';
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import CookiesPolicy from './pages/CookiesPolicy';
import CookieConsent from './components/CookieConsent';

import './App.css';

export const DevUserContext = createContext();
export const useDevUser = () => useContext(DevUserContext);

const ProtectedRoute = ({ children, requireSubscriber = false }) => {
  const { currentUser, userProfile } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (requireSubscriber && userProfile?.role !== 'subscriber') {
    return <Navigate to="/" />;
  }

  return children;
};

function AppRoutes() {
  const { isMobileApp } = usePlatform();

  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/map" element={<Map />} />
          {!isMobileApp && <Route path="/pricing" element={<Pricing />} />}
          <Route 
            path="/report" 
            element={
              <ProtectedRoute>
                <ReportWaste />
              </ProtectedRoute>
            } 
          />
          <Route path="/waste/:id" element={<WasteDetail />} />
          <Route path="/individual-dashboard" element={<IndividualDashboard />} />
          {!isMobileApp && <Route path="/corporate-dashboard" element={<CorporateDashboard />} />}
          {!isMobileApp && (
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute requireSubscriber={true}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
          )}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } 
          />
          {!isMobileApp && (
            <Route 
              path="/subscribe" 
              element={
                <ProtectedRoute>
                  <Subscribe />
                </ProtectedRoute>
              } 
            />
          )}
          {!isMobileApp && (
            <Route 
              path="/payment/success" 
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              } 
            />
          )}
          {!isMobileApp && <Route path="/super-admin/login" element={<SuperAdminLogin />} />}
          {!isMobileApp && <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />}
          <Route path="/cookies-policy" element={<CookiesPolicy />} />
        </Routes>
      </main>
      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <CookieConsent />
    </div>
  );
}

function AppWithDevSwitcher() {
  const [devUser, setDevUser] = useState(null);

  return (
    <DevUserContext.Provider value={{ devUser, setDevUser }}>
      <AppRoutes />
      <DevUserSwitcher 
        currentUserId={devUser?.uid} 
        onUserSwitch={setDevUser} 
      />
    </DevUserContext.Provider>
  );
}

function App() {
  return (
    <Router>
      <PlatformProvider>
        <AuthProvider>
          <AppWithDevSwitcher />
        </AuthProvider>
      </PlatformProvider>
    </Router>
  );
}

export default App;
